import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { InputComponent } from "@components/input/input.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import { Subject, forkJoin } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { CheckboxComponent } from "~app/common/components/checkbox/checkbox.component";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { Anskaffningsformer, ConfirmDialogResponse, Transportslag } from "~app/common/models/constants";
import { TplTransportforetagDTO } from "~app/common/models/transport/TplTransportforetagDTO";
import { EventService } from "~app/common/services/cqs/event.service";
import { ModelActionService } from "~app/common/services/cqs/model-action.service";
import { EventHubService } from "~app/common/services/eventhub.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IKundList } from "~app/features/forsaljning/kund/models/kundList";
import { KundService } from "~app/features/forsaljning/kund/services/kund.service";
import { ISearchMottagningsplatsQueryLagerplatsResult, ISearchMottagningsplatsQueryResult } from "~app/features/forsaljning/mottagningsplats/mottagningsplats.models";
import * as ArbetsorderConstants from "~app/features/planera/model/ArbetsorderConstants";
import { TransportforetagService } from "~app/features/transport/transportforetag/services/transportforetag.service";
import { ArbetsorderDistriktUpdatedEventMessage } from "../events/ArbetsorderDistriktUpdatedEventMessage";
import { SkapaArbetsorderService } from "../services/skapa-arbetsorder.service";
import { SkapaArbetsorderData } from "./models/SkapaArbetsorderData";
import { SkapaArbetsorderValidation } from "./skapa-arbetsorder.validation";

@Component({
    selector: "rask-skapa-arbetsorder-dialog",
    templateUrl: "./skapa-arbetsorder-dialog.component.html",
    styleUrls: ["./skapa-arbetsorder-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        SelectorComponent,
        InputComponent,
        RadiobuttonGroupComponent,
        DatepickerComponent,
        ButtonComponent,
        CheckboxComponent,
        TranslatePipe
    ]
})
export class SkapaArbetsorderDialogComponent implements OnDestroy, OnInit {
    allaMottagningsplater: ISearchMottagningsplatsQueryResult[];
    ckLeverantorer: any[];
    form: UntypedFormGroup;
    modal = inject(NgbActiveModal);
    skapaArbetsorderModel: SkapaArbetsorderData;
    transportforetag: TplTransportforetagDTO[];
    valbaraAnskaffningsformer = [
        Anskaffningsformer.EgenTerminal,
        Anskaffningsformer.Import
    ];
    valbaraTransportslag = Transportslag.allaMedTransport;
    validationContext: IValidationContext;
    arbetsorderNamnMaxLength = ArbetsorderConstants.ArbetsordernamnMaxLength;

    protected activetypes: IRadiobuttonItem[] = [
        {
            key: true,
            value: "Ja"
        },
        {
            key: false,
            value: "Nej"
        }
    ];

    private readonly busyService = inject(BusyService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly eventHubService = inject(EventHubService);
    private readonly eventService = inject(EventService);
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly kundService = inject(KundService);
    private readonly modelActionService = inject(ModelActionService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly skapaArbetsorderservice = inject(SkapaArbetsorderService);
    private readonly transportforetagService = inject(TransportforetagService);

    anskaffningsformChanged() {
        this.skapaArbetsorderModel.batId = undefined;
        this.changeDetectorRef.detectChanges();
        this.skapaArbetsorderModel.ckleverantorId = undefined;
        this.resetMottagningsplats();
    }

    canSave(): boolean {
        return !!this.form &&
            !this.form.disabled &&
            (this.form.dirty) &&
            this.validationContext.validate();
    }

    ckleverantorFormatter = (ckleverantor: any) => ckleverantor ? ckleverantor.id : undefined;

    ckleverantorViewFormatter = (ckleverantor: any) => ckleverantor ?
        `${ckleverantor.namn}(${ckleverantor.huvudkod}-${ckleverantor.internnummer})` : undefined;

    dismiss(): void {
        this.modal.close(ConfirmDialogResponse.cancel);
    }

    getLagerplatser(): any[] {
        const mottagningsplats = this.allaMottagningsplater.find(x => x.id === this.skapaArbetsorderModel.hamtplatsId);
        return mottagningsplats?.lagerplatser?.filter(x => x.finnsIViol3) ?? undefined;
    }

    getLagerplatserForAvgangshamn(): any[] {
        const mottagningsplats = this.allaMottagningsplater.find(x => x.id === this.skapaArbetsorderModel.mottagningsplatsAvgangshamnId);
        return mottagningsplats?.lagerplatser?.filter(x => x.finnsIViol3) ?? undefined;
    }

    getMottagningsplatserForTyp(): ISearchMottagningsplatsQueryResult[] {
        if (!ObjectUtils.isNullOrUndefined(this.skapaArbetsorderModel.anskaffningsForm)) {
            return this.skapaArbetsorderModel.anskaffningsForm === Anskaffningsformer.Import ? this.allaMottagningsplater
                : this.allaMottagningsplater.filter(x => x.egenTerminal);
        }
        return undefined;
    }

    kundFormatter = (kund: IKundList) => kund ? kund.id : undefined;

    kundViewFormatter = (kund: IKundList) => kund ?
        `${kund.namn} (${kund.huvudkod}-${kund.internnummer})` : undefined;

    lagerplatserFormatter = (lagerplats: ISearchMottagningsplatsQueryLagerplatsResult) => lagerplats ? lagerplats.id : undefined;

    lagerplatserViewFormatter = (lagerplats: ISearchMottagningsplatsQueryLagerplatsResult) => lagerplats ?
        `${lagerplats.namn}(${lagerplats.avlamningsplats})` : undefined;

    mottagningsplatsChanged() {
        this.skapaArbetsorderModel.lagerplatsId = undefined;
    }

    mottagningsplatsFormatter = (mottagningsplats: ISearchMottagningsplatsQueryResult) => mottagningsplats ? mottagningsplats.id : undefined;

    mottagningsplatsViewFormatter = (mottagningsplats: ISearchMottagningsplatsQueryResult) => mottagningsplats ?
        `${mottagningsplats.namn}(${mottagningsplats.mottagningsplatsnummer})` : undefined;

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.skapaArbetsorderModel = new SkapaArbetsorderData();
        this.skapaArbetsorderModel.transportslag = Transportslag.Vag;
        this.skapaArbetsorderModel.medBil = true;

        forkJoin([
            this.skapaArbetsorderservice.hamtaMottagningsplatser(),
            this.skapaArbetsorderservice.hamtaCkLeverantorer(),
            this.transportforetagService.getAllTransportforetag()
        ]).subscribe((result) => {
            const [
                mottagningsplatser,
                ckleverantorer,
                transportforetag
            ] = result;
            this.allaMottagningsplater = mottagningsplatser;
            this.ckLeverantorer = ckleverantorer.ckLeverantorer;
            this.transportforetag = transportforetag;

            this.modelActionService.reset();
            this.modelActionService.addNewModel(this.skapaArbetsorderModel, "AddArbetsorder");
            this.setupForm();
        });
    }

    save(): void {
        if (this.canSave()) {
            // Används för att hålla ihop spara och vänta på svar från ArbetsorderDistriktUpdatedEventMessage.
            // Om dialogen stängs före distrikt är uppdaterat uppstår concurrency sedan när man redigerar den skapade arbetsordern
            const totalWork = new Subject();

            const unregisterAndClose = (arbetsorderId: number) => {
                this.eventHubService.unregisterListener(`arbetsorder/${arbetsorderId.toString()}`);
                this.modal.close(arbetsorderId);
                totalWork.complete();
            };

            const saveObs = this.modelActionService.pushCommandQueue();
            this.busyService.addWork(totalWork.asObservable()).subscribe();

            saveObs.subscribe({
                next: r => {
                    const arbetsorderId = r[0].entityId;
                    const timeout = setTimeout(() => {
                        unregisterAndClose(arbetsorderId);
                    }, 10000);

                    this.eventHubService.registerListener(`arbetsorder/${arbetsorderId.toString()}`);
                    this.eventService.subscribeEvent<ArbetsorderDistriktUpdatedEventMessage>(ArbetsorderDistriktUpdatedEventMessage.$type)
                        .pipe(takeUntil(this.ngUnsubscribe$))
                        .subscribe(ev => {
                            clearTimeout(timeout);
                            unregisterAndClose(arbetsorderId);
                        });
                },
                error: (e) => {
                    totalWork.complete();
                }
            });
        }
    }

    searchForKunder = (searchString: string) => this.kundService.searchForKund({ sokTypeAhead: searchString }).pipe(map(x => x.kontaktKundList));

    transportforetagFormatter = (transportforetag: TplTransportforetagDTO) => transportforetag ? transportforetag.id : undefined;

    transportforetagViewFormatter = (transportforetag: TplTransportforetagDTO) => transportforetag ?
        `${transportforetag.namn}(${transportforetag.huvudkod})` : undefined;

    transportslagChanged(transportslag: string) {
        if (transportslag !== Transportslag.Sjo) {
            this.skapaArbetsorderModel.batId = undefined;
            this.skapaArbetsorderModel.avgangsmatning = null;
            this.skapaArbetsorderModel.ckleverantorId = undefined;
        }

        this.changeDetectorRef.detectChanges();
    }

    private resetMottagningsplats() {
        this.skapaArbetsorderModel.hamtplatsId = undefined;
        this.skapaArbetsorderModel.lagerplatsId = undefined;
    }

    private readonly setupForm = () => {
        this.form = this.formBuilder.group(this.skapaArbetsorderModel);
        this.validationContext = SkapaArbetsorderValidation.init(
            this.skapaArbetsorderModel);
    };
}
